/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthenticationService } from '@alfresco/adf-core';
import { switchMap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { algAuthorizationHeader } from './alg-auth';

@Injectable({
  providedIn: 'root'
})
export class AlgRenditionService {
  /** Intervalle entre deux interrogations du statut du rendu. */
  private static readonly RENDITION_POLL_MS = 2000;

  /** Au-dela, on considere que le serveur ne produira pas ce rendu. */
  private static readonly RENDITION_TIMEOUT_MS = 30000;

  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthenticationService);

  getRendition(nodeId: string) {
    const endpointUrl = window.location.origin + `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${nodeId}/renditions/pdf`;
    let headers = new HttpHeaders();
    const auth = algAuthorizationHeader(this.authService);
    headers = headers.append('Authorization', auth);

    return this.http.get(endpointUrl, { headers: headers });
  }

  getRenderedPdf(nodeId: string) {
    const endpointUrl =
      window.location.origin + `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${nodeId}/renditions/pdf/content?attachment=false`;
    let headers = new HttpHeaders();
    const auth = algAuthorizationHeader(this.authService);
    headers = headers.append('Authorization', auth);
    headers = headers.append('Content-Type', 'application/pdf;charset=UTF-8');

    const params = {
      headers: headers,
      responseType: 'blob' as 'json' // c'est la partie importante
    };
    return this.http.get(endpointUrl, params);
  }

  requestPdfRendition(nodeId: string) {
    const endpointUrl = window.location.origin + `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${nodeId}/renditions`;

    let headers = new HttpHeaders();
    const auth = algAuthorizationHeader(this.authService);
    headers = headers.append('Authorization', auth);
    headers = headers.append('Content-Type', 'application/json');

    return this.http.post(endpointUrl, { id: 'pdf' }, { headers: headers }).pipe(switchMap(() => this.checkRenditionStatus(nodeId)));
  }

  /**
   * Attend que le rendu PDF soit pret, en interrogeant le serveur.
   *
   * Trois defauts corriges, tous les trois observables a l'usage :
   *
   * - **aucun nettoyage.** `clearInterval` n'etait appele que sur CREATED ou ERROR. Si l'abonne se
   *   desabonnait — volet referme, composant detruit — l'intervalle survivait et interrogeait le
   *   serveur toutes les 3 s jusqu'a la fin de la session, un intervalle de plus par tentative de
   *   previsualisation. Le `return` de teardown y met fin.
   * - **aucune limite.** Un type que le serveur ne sait pas convertir garde le statut
   *   `NOT_CREATED` indefiniment : ni CREATED ni ERROR, donc une attente sans fin et un volet
   *   bloque sur son indicateur de chargement. D'ou le delai maximum.
   * - **aucune gestion d'erreur.** Un appel en echec dans l'intervalle produisait une erreur non
   *   traitee a chaque tour, sans jamais rien signaler a l'appelant.
   */
  checkRenditionStatus(nodeId: string): Observable<string> {
    return new Observable<string>((observer) => {
      const startedAt = Date.now();

      const intervalId = setInterval(() => {
        if (Date.now() - startedAt > AlgRenditionService.RENDITION_TIMEOUT_MS) {
          clearInterval(intervalId);
          observer.error(new Error(`Rendu PDF non disponible apres ${AlgRenditionService.RENDITION_TIMEOUT_MS} ms`));
          return;
        }

        this.getRendition(nodeId).subscribe({
          next: (rendition: any) => {
            const status = rendition?.entry?.status;
            if (status === 'CREATED') {
              clearInterval(intervalId);
              observer.next('CREATED');
              observer.complete();
            } else if (status === 'ERROR') {
              clearInterval(intervalId);
              observer.error(new Error('Le serveur a echoue a produire le rendu PDF'));
            }
          },
          error: (error) => {
            clearInterval(intervalId);
            observer.error(error);
          }
        });
      }, AlgRenditionService.RENDITION_POLL_MS);

      return () => clearInterval(intervalId);
    });
  }
}
